'use client';

import { useEffect, useState } from 'react';
import Link from "next/link";
import { Menu, X } from "lucide-react";
import UserMenu from "./UserMenu";
import ThemeToggle from "./ThemeToggle";
import PublicAuthorMenu from "./PublicAuthorMenu";

import { getHeaderAuthState, type HeaderAuthState } from "@/app/actions/auth-actions";

export default function Header() {
    const [auth, setAuth] = useState<HeaderAuthState>({ kind: 'none' });
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const nextAuth = await getHeaderAuthState();
                if (!cancelled) setAuth(nextAuth);
            } catch {
                if (!cancelled) setAuth({ kind: 'none' });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Prevent body vertical scrolling when mobile menu wrapper is active
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    const [isHeaderVisible, setIsHeaderVisible] = useState(true);

    useEffect(() => {
        let lastScrollY = window.scrollY;
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const currentScrollY = Math.max(0, window.scrollY);
                    
                    if (isMobileMenuOpen || currentScrollY <= 50) {
                        setIsHeaderVisible(true);
                    } else if (currentScrollY > lastScrollY + 10) {
                        // Scrolling down deliberately
                        setIsHeaderVisible(false);
                    } else if (currentScrollY < lastScrollY - 10) {
                        // Scrolling up deliberately
                        setIsHeaderVisible(true);
                    }
                    
                    // Only update lastScrollY if we exceeded the delta threshold,
                    // or if we are at the top, to keep track accurately
                    if (Math.abs(currentScrollY - lastScrollY) > 10 || currentScrollY <= 50) {
                        lastScrollY = currentScrollY;
                    }
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobileMenuOpen]);

    return (
        <header className={`border-b border-border-subtle bg-[var(--color-bg-base)]/90 backdrop-blur-md sticky top-0 z-40 shadow-sm transition-all duration-300 ${!isHeaderVisible ? '-translate-y-full md:translate-y-0' : 'translate-y-0'}`}>
            <div className="container flex items-center justify-between h-16 md:h-20">
                <Link href="/" className="logo flex items-center gap-3 hover:opacity-80 transition-opacity">
                    <img
                        src="/logo/carsite.png"
                        alt="CarSite Logo"
                        className="object-contain light-mode-only h-8 md:h-10 w-auto"
                    />
                    <img
                        src="/logo/carsite-white.png"
                        alt="CarSite Logo (Dark)"
                        className="object-contain dark-mode-only h-8 md:h-10 w-auto"
                    />
                    <img
                        src="/carsite-alpha-logo.png"
                        alt="Alpha Version"
                        className="object-contain light-mode-only h-6 md:h-8 w-auto hidden sm:block"
                    />
                    <img
                        src="/carsite-alpha-logo-white.png"
                        alt="Alpha Version (Dark)"
                        className="object-contain dark-mode-only h-6 md:h-8 w-auto hidden sm:block"
                    />
                </Link>

                <div className="flex items-center gap-2 md:gap-6">
                    <nav className="hidden md:flex items-center gap-6">
                        <Link href="/about-us" className="text-[15px] font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors">
                            About
                        </Link>
                        <Link href="/advertise" className="text-[15px] font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors">
                            Advertise
                        </Link>
                        <Link href="/contact" className="text-[15px] font-medium text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors">
                            Contact
                        </Link>
                    </nav>

                    <div className="hidden md:block w-px h-5 bg-border-subtle opacity-50"></div>

                    <div className="flex items-center gap-2 md:gap-3">
                        <ThemeToggle />
                        
                        {auth.kind === 'admin' ? (
                            <UserMenu user={auth.user} />
                        ) : auth.kind === 'user' ? (
                            <UserMenu user={auth.user} />
                        ) : auth.kind === 'public_author' ? (
                            <div className="hidden sm:block">
                                <PublicAuthorMenu
                                    displayName={auth.author.displayName}
                                    handle={auth.author.handle}
                                    avatarUrl={auth.author.avatarUrl}
                                    email={auth.author.email}
                                />
                            </div>
                        ) : (
                            <Link
                                href="/login"
                                className="hidden sm:block text-sm font-semibold text-[var(--color-text-main)] hover:text-[var(--color-primary)] transition-colors px-3 py-2"
                            >
                                Sign In
                            </Link>
                        )}

                        <button 
                            className="md:hidden flex items-center justify-center p-2 text-[var(--color-text-main)] hover:text-[var(--color-primary)] transition-colors"
                            onClick={() => setIsMobileMenuOpen(true)}
                            aria-label="Open mobile menu"
                        >
                            <Menu size={26} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Expanded Menu Overlay */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 bg-[var(--color-bg-base)] md:hidden flex flex-col h-[100dvh] w-full animate-in fade-in slide-in-from-top-4 duration-200">
                    <div className="flex items-center justify-between h-16 container border-b border-border-subtle">
                        <Link href="/" className="logo flex items-center gap-3" onClick={() => setIsMobileMenuOpen(false)}>
                            <img src="/logo/carsite.png" alt="CarSite Logo" className="object-contain light-mode-only h-8 w-auto" />
                            <img src="/logo/carsite-white.png" alt="CarSite Logo (Dark)" className="object-contain dark-mode-only h-8 w-auto" />
                        </Link>
                        <button 
                            className="p-2 text-[var(--color-text-main)] hover:text-[var(--color-primary)] transition-transform hover:rotate-90 duration-300"
                            onClick={() => setIsMobileMenuOpen(false)}
                            aria-label="Close mobile menu"
                        >
                            <X size={28} strokeWidth={2.5} />
                        </button>
                    </div>

                    <nav className="flex flex-col container py-8 gap-8 overflow-y-auto">
                        <Link href="/about-us" className="text-2xl font-bold text-[var(--color-text-main)] tracking-tight hover:text-[var(--color-primary)] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>About Us</Link>
                        <Link href="/advertise" className="text-2xl font-bold text-[var(--color-text-main)] tracking-tight hover:text-[var(--color-primary)] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Advertise</Link>
                        <Link href="/contact" className="text-2xl font-bold text-[var(--color-text-main)] tracking-tight hover:text-[var(--color-primary)] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Contact</Link>
                        
                        <div className="w-full h-px bg-border-subtle my-2 opacity-50"></div>
                        
                        <div className="flex items-center justify-between pb-4">
                            <span className="text-lg font-semibold text-[var(--color-text-main)]">Theme</span>
                            <ThemeToggle />
                        </div>

                        {/* Mobile Auth Inject */}
                        {auth.kind === 'admin' || auth.kind === 'user' ? (
                             <Link href="/admin" className="text-lg font-semibold text-[var(--color-primary)]" onClick={() => setIsMobileMenuOpen(false)}>My Dashboard</Link>
                        ) : auth.kind === 'public_author' ? (
                             <Link href="/author" className="text-lg font-semibold text-[var(--color-primary)]" onClick={() => setIsMobileMenuOpen(false)}>Author Dashboard</Link>
                        ) : (
                             <Link href="/login" className="flex items-center justify-center w-full py-3 rounded-md bg-[var(--color-primary)] text-white font-bold text-lg hover:bg-red-700 transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                                Sign In
                             </Link>
                        )}
                    </nav>
                </div>
            )}
        </header>
    );
}
